import React, { useCallback, useRef, useState } from 'react';
import { MdOutlineSearch } from 'react-icons/md';
import { useDebounce } from 'use-debounce';

import { useListGroups } from 'legacy/groups/hooks';
import Spinner, { SpinnerType } from 'legacy/common/components/spinner/Spinner';

import MemberOnlyRow from './MemberOnlyRow';

const OrgsOnlyAccess = ({
  isSelectionDisabled,
  selectedOrgs,
  setSelectedOrgs
}) => {
  const ref = useRef<IntersectionObserver>();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 200);

  const {
    data: orgs,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useListGroups({
    search: debouncedSearch
  });

  const lastElementRef = useCallback(
    (node: HTMLLIElement) => {
      if (isLoading || isFetchingNextPage) {
        return;
      }

      if (ref.current) {
        ref.current.disconnect();
      }

      ref.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) {
        ref.current.observe(node);
      }
    },
    [isLoading, hasNextPage]
  );

  const handleSearch = ({ currentTarget }) => {
    setSearch(currentTarget.value);
  };

  const handleSelect = (id: string) => {
    const newSelectedOrgs = [...selectedOrgs];
    const orgIndex = selectedOrgs.indexOf(id);

    if (orgIndex > -1) {
      newSelectedOrgs.splice(orgIndex, 1);
    } else {
      newSelectedOrgs.push(id);
    }

    setSelectedOrgs(newSelectedOrgs);
  };

  const clearSelectedOrgs = () => {
    setSelectedOrgs([]);
  };

  return (
    <div className="h-full pb-24 md:pb-16 overflow-auto max-h-[95vh] md:max-h-[600px]">
      <div className="p-4 pb-40">
        <div className="relative flex-shrink-0 flex h-12 border-1 border-gray-300 rounded-full mt-2">
          <MdOutlineSearch
            className="absolute left-3 text-gray-400 self-center justify-center"
            size={24}
          />
          <input
            type="text"
            className="w-full h-full px-11 rounded-full text-base text-gray-600 placeholder:text-gray-400"
            placeholder="Search organizations"
            value={search}
            onChange={handleSearch}
          />
        </div>
        <div className="py-6 flex">
          <div className="flex-1 font-bold text-base">
            {`${selectedOrgs.length} ${
              selectedOrgs.length === 1 ? 'organization' : 'organizations'
            } selected`}
          </div>
          {selectedOrgs.length > 0 && (
            <button
              type="button"
              aria-label="Clear selected organizations"
              className="font-bold text-base text-primary-500"
              onClick={clearSelectedOrgs}
            >
              Clear
            </button>
          )}
        </div>
        {isLoading ? (
          <div className="flex flex-1 h-[400px] items-center justify-center">
            <Spinner type={SpinnerType.PRIMARY} />
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {orgs.map((org) => (
              <MemberOnlyRow
                key={org.id}
                id={org.id}
                image={org.badgeImageUrl}
                name={org.name}
                isSelected={selectedOrgs.indexOf(org.id) > -1}
                onSelect={handleSelect}
                isDisabled={isSelectionDisabled}
                ref={lastElementRef}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default OrgsOnlyAccess;
